import { motion } from 'framer-motion'
import { Mail, Instagram, Facebook } from 'lucide-react'
import { InnerHero, ContactForm } from '@/components/sections'
import { Prefooter, Footer } from '@/components/layout'
import { LocalizedLink } from '@/components/LocalizedLink'

export function ContattaciPage() {
  return (
    <main>
      <InnerHero
        eyebrow="Contattaci"
        title="Parla con"
        titleItalic="Nashi Argan."
        body="Hai già consultato la sezione Domande Frequenti? Se non hai trovato risposta mandaci subito un messaggio: ti risponderemo nel più breve tempo possibile."
        breadcrumbs={[
          { label: 'Home', href: '/it' },
          { label: 'Contattaci' },
        ]}
      />

      {/* Form + side info */}
      <section
        id="form"
        className="relative bg-cream py-20 md:py-28 overflow-hidden"
        aria-label="Form di contatto"
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 left-[-4%] select-none font-display italic text-argan/[0.07]"
          style={{ fontSize: 'clamp(12rem, 26vw, 26rem)', lineHeight: 0.85 }}
        >
          n
        </div>

        <div className="relative nashi-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left rail — context + alt channels */}
            <motion.aside
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1 }}
              className="lg:col-span-4"
            >
              <div className="lg:sticky lg:top-32">
                <span className="nashi-eyebrow">Scrivici</span>
                <h2
                  className="mt-6 font-display text-bruno"
                  style={{
                    fontSize: 'clamp(1.875rem, 3vw, 2.5rem)',
                    lineHeight: 1.05,
                    letterSpacing: '-0.015em',
                  }}
                >
                  <span className="block">Siamo</span>
                  <span className="block italic font-light text-argan-deep">
                    a tua disposizione.
                  </span>
                </h2>

                <p className="mt-6 text-bruno-soft leading-relaxed text-sm md:text-base">
                  Compila il modulo per richiedere informazioni sui prodotti, i programmi
                  partner, le opportunità di lavoro o per qualsiasi altra esigenza. Il
                  nostro team ti risponderà al più presto.
                </p>

                {/* Channels */}
                <div className="mt-12 space-y-6">
                  <div className="flex items-start gap-4">
                    <span
                      aria-hidden="true"
                      className="mt-1 inline-flex w-9 h-9 items-center justify-center rounded-full border border-bruno/20 text-bruno"
                    >
                      <Mail className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-bruno-muted">
                        Domande frequenti
                      </p>
                      <a
                        href="/it/faq"
                        className="mt-1 inline-block font-display italic text-bruno hover:text-argan-deep text-lg transition-colors"
                      >
                        Vai alla sezione FAQ →
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span
                      aria-hidden="true"
                      className="mt-1 inline-flex w-9 h-9 items-center justify-center rounded-full border border-bruno/20 text-bruno"
                    >
                      <Instagram className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-bruno-muted">
                        Instagram
                      </p>
                      <a
                        href="https://www.instagram.com/nashiargan/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-block font-display italic text-bruno hover:text-argan-deep text-lg transition-colors"
                      >
                        @nashiargan
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <span
                      aria-hidden="true"
                      className="mt-1 inline-flex w-9 h-9 items-center justify-center rounded-full border border-bruno/20 text-bruno"
                    >
                      <Facebook className="h-4 w-4" strokeWidth={1.5} />
                    </span>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-bruno-muted">
                        Facebook
                      </p>
                      <a
                        href="https://www.facebook.com/NashiArganOfficial"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-block font-display italic text-bruno hover:text-argan-deep text-lg transition-colors"
                      >
                        Nashi Argan Official
                      </a>
                    </div>
                  </div>
                </div>

                {/* Editorial note */}
                <div className="mt-14 pt-8 border-t border-bruno/10">
                  <p className="font-display italic text-argan-deep text-base leading-relaxed">
                    «Ascoltiamo e rispondiamo alle esigenze con passione ed efficacia.»
                  </p>
                  <p className="mt-3 text-[10px] uppercase tracking-[0.32em] text-bruno-muted">
                    — Nashi Argan
                  </p>
                </div>
              </div>
            </motion.aside>

            {/* Right — form */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="lg:col-span-8 lg:pl-10 lg:border-l lg:border-bruno/10"
            >
              <div className="max-w-2xl">
                <span className="nashi-eyebrow">Form contatti</span>
                <h3
                  className="mt-6 mb-12 font-display text-bruno"
                  style={{
                    fontSize: 'clamp(1.5rem, 2.4vw, 2rem)',
                    lineHeight: 1.15,
                    letterSpacing: '-0.01em',
                  }}
                >
                  Compila i campi qui sotto e ti risponderemo al più presto.
                </h3>
                <ContactForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Legal info ribbon */}
      <section className="relative bg-cream-soft py-14 md:py-16 overflow-hidden">
        <div className="nashi-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-argan-deep mb-3">
                — Sede legale
              </p>
              <p className="font-display italic text-bruno text-lg leading-relaxed">
                Landoll s.r.l.
              </p>
              <p className="mt-1 text-bruno-soft text-sm leading-relaxed">
                REA: Milano | N. 2041243 Registro Imp. di MI
                <br />
                C.F. e P.IVA 01594370197
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-argan-deep mb-3">
                — Privacy
              </p>
              <p className="font-display italic text-bruno text-lg leading-relaxed">
                I tuoi dati, al sicuro.
              </p>
              <p className="mt-1 text-bruno-soft text-sm leading-relaxed">
                Trattiamo i dati personali nel rispetto della normativa GDPR. Consulta la{' '}
                <LocalizedLink
                  to="privacy"
                  className="underline underline-offset-2 hover:text-argan-deep transition-colors"
                >
                  Privacy Policy
                </LocalizedLink>{' '}
                per maggiori informazioni.
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-argan-deep mb-3">
                — Tempi di risposta
              </p>
              <p className="font-display italic text-bruno text-lg leading-relaxed">
                Entro 48 ore lavorative.
              </p>
              <p className="mt-1 text-bruno-soft text-sm leading-relaxed">
                Riceverai una risposta dal nostro team Customer Care nel più breve tempo
                possibile.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Prefooter />
      <Footer />
    </main>
  )
}
